<template>
	<div v-if="availableGroups.length" class="w-full">
		<!-- Hauptgruppen -->
		<div class="flex flex-wrap gap-2 mb-4">
			<button
				v-for="group in availableGroups"
				:key="group.group"
				type="button"
				class="px-3 py-2 rounded text-sm border"
				:class="{
					'bg-connectProBlue text-white border-connectProBlue': activeGroupName === group.group,
					'bg-white text-gray-700 border-gray-300': activeGroupName !== group.group,
				}"
				@click="selectGroup(group)"
			>
				{{ group.group }}
			</button>
		</div>

		<div v-if="activeGroup">
			<!-- Untergruppen -->
			<div v-if="activeGroup.sections && activeGroup.sections.length > 1" class="flex flex-wrap gap-2 mb-4">
				<button
					v-for="section in activeGroup.sections"
					:key="section.label"
					type="button"
					class="px-3 py-1 rounded text-sm"
					:class="{
						'bg-gray-700 text-white': activeSectionLabel === section.label,
						'bg-gray-200 text-gray-700': activeSectionLabel !== section.label,
					}"
					@click="activeSectionLabel = section.label"
				>
					{{ section.label }}
				</button>
			</div>

			<!-- Elemente -->
			<div v-if="activeSection">
				<div v-if="activeGroup.sections.length === 1" class="font-medium text-sm text-gray-600 mb-2">
					{{ activeSection.label }}
				</div>

				<div class="flex flex-wrap gap-2">
					<button
						v-for="entry in activeSection.entries"
						:key="`${entry.type}-${entry.key}`"
						type="button"
						:disabled="disabled"
						class="px-3 py-2 bg-gray-100 border border-gray-300 rounded text-sm hover:bg-gray-200"
						:title="entry.key"
						@click="!disabled && $emit('select', entry)"
					>
						<div class="font-medium">
							{{ entry.label }}
						</div>
						<div class="document-element-key">{{ formatKeyLabel(entry.key) }}</div>
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: 'DocumentElementPicker',

	props: {
		elements: {
			type: Array,
			default: () => [],
		},
		disabled: {
			type: Boolean,
			default: false,
		},
	},

	data() {
		return {
			activeGroupName: null,
			activeSectionLabel: null,
		};
	},

	computed: {
		availableGroups() {
			return this.elements || [];
		},

		activeGroup() {
			if (!this.activeGroupName) {
				return null;
			}

			return this.availableGroups.find(group => group.group === this.activeGroupName) || null;
		},

		activeSection() {
			if (!this.activeGroup || !this.activeSectionLabel) {
				return null;
			}

			return this.activeGroup.sections.find(section => section.label === this.activeSectionLabel) || null;
		},
	},

	watch: {
		elements: {
			immediate: true,
			handler() {
				this.initializeSelection();
			},
		},
	},

	methods: {
		initializeSelection() {
			if (!this.availableGroups.length) {
				this.activeGroupName = null;
				this.activeSectionLabel = null;
				return;
			}

			const currentGroupStillExists = this.availableGroups.some(group => group.group === this.activeGroupName);

			if (!currentGroupStillExists) {
				this.selectGroup(this.availableGroups[0]);
				return;
			}

			this.ensureActiveSection();
		},

		selectGroup(group) {
			this.activeGroupName = group.group;
			const firstSection = group.sections && group.sections.length ? group.sections[0] : null;
			this.activeSectionLabel = firstSection ? firstSection.label : null;
		},

		ensureActiveSection() {
			if (!this.activeGroup) {
				return;
			}

			const sectionStillExists = this.activeGroup.sections.some(section => section.label === this.activeSectionLabel);

			if (!sectionStillExists) {
				this.activeSectionLabel = this.activeGroup.sections[0]?.label || null;
			}
		},

		formatKeyLabel(key) {
			return `{{ ${key} }}`;
		},
	},
};
</script>

<style scoped>
.document-element-entry {
	padding: 10px 12px;
	border: 1px solid #d1d5db;
	border-radius: 4px;
	background: white;
	text-align: left;
}

.document-element-entry:hover {
	background: #f3f4f6;
}

.document-element-key {
	margin-top: 3px;
	font-family: monospace;
	font-size: 11px;
	color: #6b7280;
}
</style>
